/* eslint-disable no-unneeded-ternary */
import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Avatar,
  ClickAwayListener,
  Button,
  CircularProgress,
  Tooltip,
} from '@material-ui/core';
import Switch from 'react-switch';
import { DeveloperMode } from '@material-ui/icons';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import api from '~/services';
import * as actions from '~/store/actions';
import * as NuacomComponent from '~/components';
import messagesToast from '~/validations/messagesNotifications';
import * as lib from '~/dependencies';
import {
  truncate,
  log,
  formatNumber,
  hasOnlyNumber,
  electron,
  getInitials,
  eventTracker,
} from '~/helpers';
import * as Loaders from '~/components/Loaders';
import { useOutboundNumbers, useChangeAccount } from '~/hooks';
import {
  RingToneIcon,
  AudioDevicesIcon,
  ConfigCallIcon,
  AccountIcon,
  LogoutIcon,
  IconTranslation,
  LifeRingIcon,
  PeopleIcon,
} from '~/assets/svgIcons';
import { findFlag } from '~/helpers/countryCodeAndFlagSelected';
import * as actionTypes from '~/store/actions/actionTypes';
import * as locales from '~/i18n/locales';
import Accounts from './components/accounts';
import CallerIds from './components/callerIds';
import DeviceManager from '../../components/DeviceManager';
import Developer from './components/developerMode';
import SelectRingTones from './components/SelectRingTones';
import Idioms from './components/idioms';
import GetHelp from './components/GetHelp';
import DynamicQueues from './components/DynamicQueues';
import {
  Wrapper,
  TopBar,
  BtnLogout,
  BoxInformations,
  Container,
  ListOptions,
  ItemsOptions,
  InformationBox,
  TopButtons,
  SwitchGroup,
} from './styles';

export default function Configuration(props) {
  // PROPS
  const { history } = props;

  // SELECTORS
  const sipAuth = useSelector(state => state.phone?.sipCredentials);
  const userInfo = useSelector(state => state.auth);
  const status = useSelector(state => state.system?.status);
  const phone = useSelector(state => state.phone);
  const startOnBoot = useSelector(state => state.system?.startOnBoot);
  const system = useSelector(state => state.system);
  const dynamicQueues = useSelector(state => state.dynamicQueues.data);

  const changeAccountFunc = useChangeAccount();

  // CONSTS
  const showStartOnBoot = window.isElectron;
  const currentLang = localStorage.getItem('i18nextLng');
  const langs = Object.values(locales).filter(e => e.lang === currentLang);
  const langFormated = Object.assign({}, ...langs);
  const hasCallerId = hasOnlyNumber(userInfo?.outbound_cli);

  // HOOKS
  const dispatch = useDispatch();
  const timer = useRef(null);
  const [outboundNumbers, setOutboundActions] = useOutboundNumbers([]);
  const { t, i18n } = lib.useTranslation();

  // STATES
  const [loading, setLoading] = useState(false);
  const [tryToConnect, setTryToConnect] = useState(false);
  const [containerHeight, setContainerHeight] = useState(330);
  const [selectAccounts, setSelectAccounts] = useState(false);
  const [selectCallerIds, setSelectCallerIds] = useState(false);
  const [selectDevices, setSelectDevices] = useState(false);
  const [selectRingTone, setSelectRingTone] = useState(false);
  const [selectLanguage, setSelectLanguage] = useState(false);
  const [selectGetHelp, setSelectGetHelp] = useState(false);
  const [selectDynamicQueues, setSelectDynamicQueues] = useState(false);
  const [developerMode, setDeveloperMode] = useState(false);
  const [devMenu, setDevMenu] = useState(false);
  const [countClick, setCountClick] = useState(0);
  const [showInformations, setShowInformations] = useState(false);
  const [currentLng, setCurrentLng] = useState(langFormated?.languageName);
  const [switchingStatus, setSwitchingStatus] = useState(false);
  const [statusErrMsg, setStatusErrMsg] = useState(null);

  // EFFECTS
  useEffect(() => {
    // Each menu item is iqual to 47px
    if (devMenu && showStartOnBoot) {
      setContainerHeight(425);
    } else if (devMenu && !showStartOnBoot) {
      setContainerHeight(378);
    } else if (!devMenu && showStartOnBoot) {
      setContainerHeight(378);
    } else {
      setContainerHeight(331);
    }
  }, [devMenu, showStartOnBoot]);

  useEffect(() => {
    async function getValue() {
      try {
        if (window.isElectron) {
          const value = await window.ipcRenderer.invoke('getStartOnBoot');
          dispatch(actions.setStartOnBoot(value));
        }
      } catch (err) {
        log.error('Failed to involke Electron IPC getStartOnBoot.', err);
      }
    }

    getValue();
  }, [dispatch]);

  useEffect(() => {
    setOutboundActions('fetch');

    return () => {
      setCountClick(0);
      setShowInformations(false);
    };
  }, [setOutboundActions, setCountClick]);

  useEffect(() => {
    dispatch(actions.fetchAccountInfo());
    dispatch(actions.fetchAgentQueues());
    setOutboundActions('fetch');
  }, [selectDevices, setOutboundActions, dispatch]);

  useEffect(() => {
    if (
      userInfo &&
      userInfo !== 'unknown' &&
      loading &&
      !selectAccounts &&
      !selectCallerIds
    ) {
      setLoading(false);
    }
    return () => {
      if (timer.current) {
        clearTimeout(timer.current);
        timer.current = null;
      }
    };
  }, [userInfo, setLoading, loading, selectAccounts, selectCallerIds]);

  useEffect(() => {
    if (countClick === 3) {
      setShowInformations(true);
      setDevMenu(true);
    }
  }, [countClick, setShowInformations, setDevMenu]);

  async function logout() {
    setTimeout(() => {
      electron.changePhoneStatus('Unavailable', false);
    }, 3000);

    eventTracker(events => events.SignOutClicked);

    try {
      await api.auth.post.revokeToken();
    } catch (err) {
      log.error(`Failure to do log out. Reason: `, err);
      lib.ToastMessage('error', messagesToast().SETTINGS.logout.error, 7000);

      return;
    }

    history.push('/logout');
  }

  function selectAccount() {
    setSelectAccounts(!selectAccounts);
    eventTracker(events => events.AccountClicked);
  }

  function selectCallerId(value) {
    setSelectCallerIds(value);
  }

  async function changeAccount(accountValue) {
    try {
      setLoading(true);
      await changeAccountFunc(accountValue);
      dispatch(actions.resetCalls());
      dispatch(actions.resetDynamicQueues());
      dispatch(actions.fetchAgentQueues());
      dispatch(actions.resetConversations());
      setOutboundActions('fetch');
      setLoading(false);
      selectAccount();
      lib.ToastMessage(
        'success',
        messagesToast().SYSTEM.account.change.success
      );
    } catch (err) {
      log.error(`Failure to change the user account. Reason: `, err);
      setLoading(false);
      selectAccount();
      lib.ToastMessage(
        'error',
        messagesToast().SYSTEM.account.change.error,
        7000
      );
    }
  }

  async function changeOutgoing(number) {
    try {
      setLoading(true);
      await api.accounts.post.outboundNumberChange(number);
      dispatch(actions.fetchAccountInfo());

      setTimeout(() => {
        setLoading(false);
        lib.ToastMessage(
          'success',
          messagesToast().SYSTEM.callerId.change.success
        );

        setSelectCallerIds(false);
      }, 1000);
    } catch (err) {
      log.error('Failure to change the outgoing number. ', err);
    } finally {
      eventTracker(events => events.SettingsChangeCallerID);
    }
  }

  function showInformationsFunc(action) {
    if (action) {
      if (countClick < 3) {
        setCountClick(countClick + 1);
      }
    } else {
      setCountClick(0);
      setShowInformations(false);
    }
  }

  function activeDevMode(value) {
    dispatch(actions.devMode(value));
  }

  function activeConsole(value) {
    dispatch(actions.Console(value));
  }

  async function handleCheck(isBusy) {
    try {
      setStatusErrMsg(null);
      setSwitchingStatus(true);
      if (!isBusy) {
        dispatch(actions.setIsBusy(false));
        await phone.ua?.register();
      } else {
        dispatch(actions.setIsBusy(true));
        dispatch(actions.statusSystem(actionTypes.STATUS.BUSY));
        await phone.ua?.unregister();
      }
      setSwitchingStatus(false);
    } catch (err) {
      log.error(`Failure to change the status.`, err);
      // Reset to the previous registration state.
      if (isBusy) {
        setStatusErrMsg(t('screens.configuration.busyFailed'));
        dispatch(actions.setIsBusy(false));
        dispatch(actions.statusSystem(actionTypes.STATUS.AVAILABLE));
      } else {
        dispatch(actions.setIsBusy(false));
        dispatch(actions.statusSystem(actionTypes.STATUS.BUSY));
      }

      setSwitchingStatus(false);
    }
  }

  async function handleStartOnBoot(state) {
    if (window.isElectron) {
      const value = await window.ipcRenderer.invoke('setStartOnBoot', state);
      dispatch(actions.setStartOnBoot(value));
    }
  }

  function closeAll() {
    setSelectAccounts(false);
    setSelectCallerIds(false);
    setSelectDevices(false);
    setSelectRingTone(false);
    setDeveloperMode(false);
    setSelectLanguage(false);
    setSelectGetHelp(false);
    setSelectDynamicQueues(false);
    setDevMenu(false);
  }

  function tryToRegister() {
    setTryToConnect(true);
    timer.current = setTimeout(() => {
      phone.ua?.transportReconnect();
      clearTimeout(this);
    }, 10000);
  }

  function changeLanguage(lng, langName) {
    if (lng) {
      i18n.changeLanguage(lng);
      setCurrentLng(langName);
      eventTracker(events => events.ChangeLanguage);
    }
  }

  function handleOpenAudiodevices() {
    setSelectDevices(true);
    eventTracker(events => events.AudioDeviceClicked);
  }

  function handleOpenSounds() {
    setSelectRingTone(true);
    eventTracker(events => events.SoundsClicked);
  }

  function handleOpenGetHelp() {
    setSelectGetHelp(true);
    eventTracker(events => events.GetHelpClicked);
  }

  /** This function is responsible to format the queue names in case the agent is in more than one queue.
   *  It will show the queue description "Name" if it exists, if not it will show the queue number e.g., Q: 25, Q: 30... */

  function formatQueueName() {
    if (dynamicQueues.length) {
      const queueNamesListStr = dynamicQueues
        .map(q =>
          q.queue?.description ? q.queue.description : `Q${q.queue.number}`
        )
        .join()
        .replaceAll(',', ', ');

      return queueNamesListStr;
    }

    return 'No Queues';
  }

  const acc =
    (sipAuth && sipAuth.user?.substr(0, sipAuth.user.length - 3)) || '';

  function showOutboundCallerNumberAsNational() {
    const outboundCli = String(`+${userInfo?.outbound_cli?.slice(2)}`);

    const outboundNumberDetails = parsePhoneNumberFromString(outboundCli);

    if (!outboundNumberDetails) {
      return '';
    }

    if (outboundNumberDetails?.country === userInfo?.userCountry) {
      return outboundNumberDetails.formatNational();
    }

    return outboundNumberDetails.formatInternational();
  }

  const countryName =
    userInfo &&
    !!userInfo.outbound_cli &&
    parsePhoneNumberFromString(String(`+${userInfo.outbound_cli.slice(2)}`));

  const countryCode = formatNumber(
    userInfo && !!userInfo.outbound_cli,
    'countryCode'
  );

  let flag;
  if (countryName && countryName.country) {
    flag = findFlag(countryName.country, true);
  } else if (countryCode) {
    flag = findFlag(countryCode);
  } else {
    flag = null;
  }

  return (
    <>
      <NuacomComponent.Header {...props}>
        {!selectAccounts &&
        !selectCallerIds &&
        !selectDevices &&
        !developerMode &&
        !selectRingTone &&
        !selectLanguage &&
        !selectGetHelp &&
        !selectDynamicQueues ? (
          <BtnLogout type="button" onClick={logout}>
            <lib.ReactSVG src={LogoutIcon} />
            <span>{t('screens.configuration.signout')}</span>
          </BtnLogout>
        ) : (
          <TopButtons>
            <Button fullWidth onClick={() => closeAll()}>
              {t('screens.configuration.back')}
            </Button>
            <Button
              className="active"
              fullWidth
              onClick={() => {}}
              style={{ display: 'none' }}>
              {t('screens.configuration.save')}
            </Button>
          </TopButtons>
        )}
      </NuacomComponent.Header>
      <Wrapper>
        {!selectAccounts &&
          !selectCallerIds &&
          !selectDevices &&
          !developerMode &&
          !selectLanguage &&
          !selectRingTone &&
          !selectGetHelp &&
          !selectDynamicQueues && (
            <TopBar>
              <Avatar className="avatar">
                {loading ? (
                  <Loaders.Avatar size="94" />
                ) : (
                  <span>{getInitials(userInfo.user_name)}</span>
                )}
              </Avatar>
            </TopBar>
          )}
        {!selectAccounts &&
          !selectCallerIds &&
          !selectDevices &&
          !developerMode &&
          !selectLanguage &&
          !selectRingTone &&
          !selectGetHelp &&
          !selectDynamicQueues && (
            <BoxInformations checked={!system?.isBusy} lang={i18n.language}>
              <div className="titles">
                {loading ? (
                  <>
                    <Loaders.Line size="70" />
                    <Loaders.Line size="140" />
                  </>
                ) : (
                  <>
                    <span>{userInfo.user_name}</span>
                    <span>{userInfo.company_name}</span>
                  </>
                )}
              </div>
              <div className="boxStatus">
                {status !== actionTypes.STATUS.UNAVAILABLE ? (
                  <>
                    <span className="desc">
                      {!system?.isBusy
                        ? t('screens.configuration.available')
                        : t('screens.configuration.busy')}
                    </span>
                    <span
                      className="desc-off"
                      role="presentation"
                      onClick={() => {
                        if (!switchingStatus) {
                          handleCheck(!system?.isBusy);
                        }
                      }}>
                      {system?.isBusy
                        ? t('screens.configuration.available')
                        : t('screens.configuration.busy')}
                    </span>
                    <SwitchGroup>
                      <Switch
                        checked={system?.isBusy}
                        onChange={handleCheck}
                        className="react-switch"
                        id="icon-switch"
                        disabled={switchingStatus}
                      />
                      {switchingStatus && (
                        <CircularProgress className="loading-switch" />
                      )}
                    </SwitchGroup>
                  </>
                ) : (
                  <div className="try-to-register">
                    {phone.error !== 'You are not register.' && (
                      <span>{phone.error}</span>
                    )}
                    {!tryToConnect ? (
                      <>
                        <span role="presentation">
                          {t('screens.configuration.connFailure')}
                        </span>

                        <span
                          role="presentation"
                          style={{ width: 250, textAlign: 'center' }}
                          onClick={tryToRegister}>
                          {t('screens.configuration.retryMsg')}
                        </span>
                      </>
                    ) : (
                      <CircularProgress className="loading" />
                    )}
                  </div>
                )}
              </div>
              {statusErrMsg && status !== actionTypes.STATUS.UNAVAILABLE && (
                <p className="err-msg">{statusErrMsg}</p>
              )}
            </BoxInformations>
          )}
        <Container>
          {!selectAccounts &&
            !selectCallerIds &&
            !selectDevices &&
            !developerMode &&
            !selectRingTone &&
            !selectLanguage &&
            !selectGetHelp &&
            !selectDynamicQueues && (
              <ListOptions height={containerHeight}>
                <ItemsOptions fullWidth onClick={() => selectAccount()}>
                  <div className="row">
                    <span>{t('screens.configuration.account')}</span>
                    {loading ||
                    (userInfo &&
                      (!!userInfo.company_name === false ||
                        userInfo.company_name === 'unknown')) ? (
                      <Loaders.Line size="150" />
                    ) : (
                      <span>
                        {`${truncate(
                          userInfo.company_name,
                          21
                        )} | ${userInfo?.account_id?.replace('IE-', '')}-`}
                        <b>{userInfo.user_extension}</b>
                      </span>
                    )}
                  </div>
                  <lib.ReactSVG src={AccountIcon} className="icon" />
                </ItemsOptions>
                <ItemsOptions
                  fullWidth
                  disabled={outboundNumbers?.numbers?.length > 1 ? false : true}
                  onClick={() => selectCallerId(true)}>
                  <div className="row select-caller-id">
                    <span>{t('screens.configuration.selectCallerId')}</span>
                    {loading || !hasCallerId ? (
                      <span>Not Set (unknown/private)</span>
                    ) : (
                      <span>
                        {userInfo.outbound_cli && flag && (
                          <img
                            src={flag}
                            alt="flag"
                            style={{ maxWidth: 10, marginRight: 5 }}
                          />
                        )}
                        {showOutboundCallerNumberAsNational()}
                      </span>
                    )}
                  </div>
                  <lib.ReactSVG src={ConfigCallIcon} className="icon" />
                </ItemsOptions>
                <ItemsOptions
                  fullWidth
                  disabled={dynamicQueues?.length ? false : true}
                  onClick={() => setSelectDynamicQueues(true)}>
                  <div className="row">
                    <span>Queues</span>
                    <Tooltip title={formatQueueName()}>
                      <span>{truncate(formatQueueName(), 50)}</span>
                    </Tooltip>
                  </div>
                  <lib.ReactSVG src={PeopleIcon} className="icon" />
                </ItemsOptions>
                <ItemsOptions fullWidth onClick={() => setSelectLanguage(true)}>
                  <div className="row">
                    <span>{t('screens.configuration.language')}</span>
                    <span>{langFormated?.languageName}</span>
                  </div>
                  <lib.ReactSVG src={IconTranslation} className="icon" />
                </ItemsOptions>
                <ItemsOptions fullWidth onClick={handleOpenAudiodevices}>
                  <div className="row">
                    <span>{t('screens.configuration.audioDevice')}</span>
                    <span />
                  </div>
                  <lib.ReactSVG src={AudioDevicesIcon} className="icon" />
                </ItemsOptions>
                <ItemsOptions fullWidth onClick={handleOpenSounds}>
                  <div className="row">
                    <span>{t('screens.configuration.sounds')}</span>
                    <span />
                  </div>
                  <lib.ReactSVG src={RingToneIcon} className="icon" />
                </ItemsOptions>
                <ItemsOptions fullWidth onClick={handleOpenGetHelp}>
                  <div className="row">
                    <span>
                      {t(
                        'screens.configuration.components.getHelp.getHelpText'
                      )}
                    </span>
                    <span />
                  </div>
                  <lib.ReactSVG src={LifeRingIcon} className="icon" />
                </ItemsOptions>
                {showStartOnBoot && (
                  <ItemsOptions fullWidth>
                    <div className="row">
                      <span>{t('screens.configuration.startOnBoot')}</span>
                      <span />
                    </div>
                    <Switch
                      checked={startOnBoot}
                      onChange={handleStartOnBoot}
                      id="icon-switch"
                      height={22}
                      width={40}
                      uncheckedIcon={false}
                      checkedIcon={false}
                      onColor="#29CF6A"
                    />
                  </ItemsOptions>
                )}
                {devMenu && (
                  <ItemsOptions
                    fullWidth
                    onClick={() => setDeveloperMode(true)}>
                    <div className="row">
                      <span>Developer mode</span>
                      <span />
                    </div>
                    <DeveloperMode />
                  </ItemsOptions>
                )}
              </ListOptions>
            )}

          {selectAccounts && (
            <Accounts
              accounts={userInfo.user_shared_accounts}
              userInfo={userInfo}
              loading={loading}
              changeAccount={changeAccount}
              acc={acc}
            />
          )}

          {selectCallerIds && (
            <CallerIds
              outboundNumbers={outboundNumbers}
              loading={loading}
              userInfo={userInfo}
              changeOutgoing={changeOutgoing}
            />
          )}

          {selectDynamicQueues && <DynamicQueues />}

          {developerMode && (
            <Developer
              system={system}
              activeDevMode={activeDevMode}
              activeConsole={activeConsole}
              setDeveloperMode={setDeveloperMode}
              setDevMenu={setDevMenu}
            />
          )}

          {selectRingTone && (
            <SelectRingTones setSelectRingTone={setSelectRingTone} />
          )}

          {selectLanguage && (
            <Idioms
              languageName={currentLng}
              handleChangeLng={changeLanguage}
              loading={loading}
            />
          )}

          {selectGetHelp && <GetHelp />}

          {selectDevices && (
            <DeviceManager setSelectDevices={setSelectDevices} />
          )}
        </Container>

        <InformationBox>
          <ClickAwayListener onClickAway={() => showInformationsFunc(false)}>
            {!showInformations ? (
              <div
                className="description"
                onClick={() => showInformationsFunc(true)}
                onKeyPress={() => showInformationsFunc(true)}
                role="presentation">
                {/* <span>{t('screens.configuration.version')}</span> */}
                <span>v</span>
                <span>{system && system.version && system.version}</span>
              </div>
            ) : (
              <div className="description">
                <span>Machine ID: </span>
                <span>{system.machineId}</span>
              </div>
            )}
          </ClickAwayListener>
        </InformationBox>
      </Wrapper>
    </>
  );
}

Configuration.defaultProps = {
  history: [],
};

Configuration.propTypes = {
  history: lib.PropTypes.instanceOf(Object),
};
